/**
 * CUSTOM ELEMENT INJECTION SYSTEM
 *
 * This utility provides a powerful and versatile system for injecting custom JSX
 * components into report bodies at runtime. It allows different report types to define
 * their own custom visualizations, analyses, or any React component and place them at
 * specific positions within the report structure without modifying the core report
 * rendering logic.
 *
 * DESIGN PRINCIPLES:
 * - Non-destructive: Original data structure remains intact
 * - Flexible positioning: Insert using first, after, or last positioning
 * - Type-safe: Component registry prevents missing component errors
 * - Clean separation: Custom logic is isolated from core rendering
 * - JSON-driven: Configuration can be easily modified without code changes
 * - Versatile: Each report type can have its own injection configuration
 * - Reusable: Same injection system works across different report types
 * - Modular: Component registries and configs are co-located with their report types
 * - Registry-required: Always requires a component registry parameter (no global fallback)
 *
 * INJECTION TYPES:
 * 1. custom_plots: Inject custom components within existing subsections
 * 2. custom_subsections: Inject entirely new subsections with custom content
 *
 * POSITIONING LOGIC:
 * - Priority order: "first" > "after" > "last"
 * - If "first" is true, insert at the beginning
 * - If "after" is specified, insert after the named element
 * - If "last" is true, insert at the end
 *
 * WHY THIS VERSATILE APPROACH:
 * - Different report types need different custom visualizations
 * - Clients can have report-specific custom components
 * - Backend defines general structure but doesn't know about custom UI per report type
 * - Frontend can define positioning and custom components declaratively per report
 * - Maintains clean architecture with clear separation of concerns
 * - Configuration and registry are passed from parent components, enabling reusability
 * - Co-location: Each report type keeps its config and registry together
 * - Modularity: Changes to one report type don't affect others
 */

import React from 'react'
import type {
  ReportDocument,
  PlotGroup,
  PlotPackage,
  PlotlyContent,
} from '../../../../../../api/generated/models'

// Custom Element Insertion Utility
// Handles the logic for injecting custom JSX elements into report reportDocument structure
// Requires injection configuration and component registry as parameters - no global dependencies

/**
 * Component registry type - maps component type strings to React components
 */
export type ComponentRegistry = {
  [key: string]: React.ComponentType<any>
}

/**
 * Base positioning configuration for injection rules
 */
interface BasePositioning {
  first?: boolean
  after?: string
  last?: boolean
}

/**
 * Configuration for injecting custom plots within existing subsections
 */
export interface CustomPlotConfig extends BasePositioning {
  inside_subsection: string
  title: string
  description: string
  type: string
  data?: any
}

/**
 * Configuration for injecting custom subsections
 */
export interface CustomSubsectionConfig extends BasePositioning {
  title: string
  description: string
  type: string
  data?: any
  flex?: string
}

/**
 * Main injection configuration structure
 */
export interface InjectionConfig {
  custom_plots?: CustomPlotConfig[]
  custom_subsections?: CustomSubsectionConfig[]
}

/**
 * Extended PlotlyContent interface that includes custom JSX support
 * Now much simpler since the generated types include optional fields
 */
interface ExtendedPlotlyContent extends PlotlyContent {
  is_custom?: boolean
  type?: string
  // data is already optional in the generated PlotlyContent type
}

/**
 * Extended PlotPackage interface that supports custom plots
 * The generated type now includes the id field, so less extension needed
 */
interface ExtendedPlotPackage extends PlotPackage {
  plot?: ExtendedPlotlyContent
}

/**
 * Extended PlotGroup interface that supports custom subsections and plots
 * The generated type now includes id and optional fields
 */
interface ExtendedPlotGroup extends PlotGroup {
  plots?: ExtendedPlotPackage[]
  is_custom?: boolean
}

/**
 * Extended ReportDocument interface that supports custom elements
 * Much simpler now that the base type has optional plots
 */
export interface ExtendedReportDocument extends ReportDocument {
  plots?: ExtendedPlotGroup[]
}

/**
 * Custom JSX configuration extracted from plot data
 */
export interface CustomJSXConfig {
  type: string
  data: any
}

/**
 * Helper function to get component by type from a component registry
 *
 * This function maps string identifiers to actual React components, providing
 * a clean way to dynamically resolve components at runtime. It includes error
 * handling for missing components to prevent runtime crashes.
 *
 * @param type - The component type string (e.g., 'custom-component')
 * @param componentRegistry - The component registry to use (required)
 * @returns The component or null if not found
 */
export const getComponentByType = (
  type: string,
  componentRegistry: ComponentRegistry,
): React.ComponentType<any> | null => {
  if (!componentRegistry) {
    console.error('Component registry is required but not provided')
    return null
  }

  const Component = componentRegistry[type]
  if (!Component) {
    console.warn(`Custom component type "${type}" not found in registry`)
    return null
  }
  return Component
}

/**
 * MAIN INJECTION ORCHESTRATOR
 *
 * This is the primary function that processes a report reportDocument and applies all
 * custom element injections based on the configuration. It maintains the
 * original data structure while adding custom elements at specified positions.
 *
 * PROCESS:
 * 1. Clone original reportDocument to avoid mutations
 * 2. Inject custom plots within existing subsections
 * 3. Inject custom subsections into the plots array
 *
 * The order matters: plots first, then subsections, to ensure proper positioning.
 *
 * @param reportDocument - The original report reportDocument with plots structure
 * @param injectionConfig - The injection configuration object
 * @param componentRegistry - The component registry for resolving component types
 * @returns Modified reportDocument with custom elements injected
 */
export const insertCustomElements = (
  reportDocument: ReportDocument,
  injectionConfig: InjectionConfig,
  componentRegistry: ComponentRegistry,
): ExtendedReportDocument => {
  if (!reportDocument || !reportDocument.plots || !injectionConfig) {
    return reportDocument as ExtendedReportDocument
  }

  // Clone the reportDocument to avoid mutating the original
  const modifiedBody: ExtendedReportDocument = JSON.parse(
    JSON.stringify(reportDocument),
  )

  // Ensure plots array exists
  if (!modifiedBody.plots) {
    modifiedBody.plots = []
  }

  // First, inject custom plots within existing subsections
  modifiedBody.plots = injectCustomPlots(
    modifiedBody.plots,
    injectionConfig.custom_plots || [],
    componentRegistry,
  )

  // Then, inject custom subsections
  modifiedBody.plots = injectCustomSubsections(
    modifiedBody.plots,
    injectionConfig.custom_subsections || [],
    componentRegistry,
  )

  return modifiedBody
}

/**
 * CUSTOM PLOT INJECTION
 *
 * Injects custom components within existing subsections. This allows adding
 * custom visualizations alongside regular plots without creating new sections.
 *
 * POSITIONING LOGIC:
 * - Priority order: "first" > "after" > "last"
 * - If "first" is true, insert at the beginning
 * - If "after" is specified, insert after the named plot
 * - If "last" is true, insert at the end
 *
 * EXAMPLE USE CASE:
 * - Adding a custom commentary widget after "Model fit score"
 * - Inserting client-specific visualizations between standard plots
 *
 * @param plots - Array of plot subsections
 * @param customPlots - Array of custom plot injection rules
 * @param componentRegistry - The component registry for resolving component types
 * @returns Modified plots array with custom plots injected
 */
const injectCustomPlots = (
  plots: ExtendedPlotGroup[],
  customPlots: CustomPlotConfig[],
  componentRegistry: ComponentRegistry,
): ExtendedPlotGroup[] => {
  // Check if all specified subsections exist and log errors for missing ones
  customPlots.forEach((customPlot) => {
    const subsectionExists = plots.some(
      (subsection) => subsection.title === customPlot.inside_subsection,
    )
    if (!subsectionExists) {
      console.error(
        `Custom plot injection failed: Subsection "${customPlot.inside_subsection}" not found. Available subsections: ${plots.map((s) => s.title).join(', ')}`,
      )
    }
  })

  return plots.map((subsection) => {
    // Find custom plots that should be injected into this subsection
    const relevantCustomPlots = customPlots.filter(
      (customPlot) => customPlot.inside_subsection === subsection.title,
    )

    if (relevantCustomPlots.length === 0) {
      return subsection
    }

    // Clone the subsection to avoid mutation, ensuring plots array exists
    const modifiedSubsection: ExtendedPlotGroup = {
      ...subsection,
      plots: [...(subsection.plots || [])],
    }

    // Ensure plots array exists
    if (!modifiedSubsection.plots) {
      modifiedSubsection.plots = []
    }

    // Process each custom plot injection for this subsection
    relevantCustomPlots.forEach((customPlot) => {
      // Create the custom plot object with is_custom flag for easy identification
      const customPlotObject: ExtendedPlotPackage = {
        title: customPlot.title,
        description: customPlot.description,
        ai_description: '', // Can be empty string now that it's optional
        plot: {
          is_custom: true, // Key flag that tells renderer this is custom
          type: customPlot.type,
          data: customPlot.data ? [customPlot.data] : [], // Optional data array
          layout: {}, // Optional layout object
        },
      }

      // Determine positioning based on priority: first > after > last
      if (customPlot.first) {
        // Insert at the beginning
        modifiedSubsection.plots!.unshift(customPlotObject)
      } else if (customPlot.after) {
        // Insert after the specified plot
        const afterIndex = modifiedSubsection.plots!.findIndex(
          (plot) => plot.title === customPlot.after,
        )

        if (afterIndex !== -1) {
          modifiedSubsection.plots!.splice(afterIndex + 1, 0, customPlotObject)
        } else {
          console.warn(
            `Plot "${customPlot.after}" not found in subsection "${subsection.title}"`,
          )
        }
      } else if (customPlot.last) {
        // Insert at the end
        modifiedSubsection.plots!.push(customPlotObject)
      } else {
        // Default: insert at the end if no positioning specified
        modifiedSubsection.plots!.push(customPlotObject)
      }
    })

    return modifiedSubsection
  })
}

/**
 * CUSTOM SUBSECTION INJECTION
 *
 * Creates entirely new subsections that appear in the main report flow.
 * These subsections contain custom components and are indistinguishable
 * from regular subsections in the final render.
 *
 * POSITIONING LOGIC:
 * - Priority order: "first" > "after" > "last"
 * - If "first" is true, insert at the beginning
 * - If "after" is specified, insert after the named subsection
 * - If "last" is true, insert at the end
 *
 * EXAMPLE USE CASE:
 * - Adding a custom analysis section after "Model summary"
 * - Inserting client-specific reporting sections
 * - Adding interactive dashboards or forms
 *
 * @param extendedPlotGroups - Array of plot subsections
 * @param customSubsections - Array of custom subsection injection rules
 * @param componentRegistry - The component registry for resolving component types
 * @returns Modified plots array with custom subsections injected
 */
const injectCustomSubsections = (
  extendedPlotGroups: ExtendedPlotGroup[],
  customSubsections: CustomSubsectionConfig[],
  componentRegistry: ComponentRegistry,
): ExtendedPlotGroup[] => {
  const modifiedPlots = [...extendedPlotGroups]

  customSubsections.forEach((customSubsection) => {
    // Create the custom subsection object with dual is_custom flags
    const customSubsectionObject: ExtendedPlotGroup = {
      title: customSubsection.title,
      description: customSubsection.description,
      flex: customSubsection.flex || 'column',
      is_custom: true, // Flag at subsection level for quick identification
      plots: [
        {
          title: customSubsection.title,
          description: customSubsection.description,
          ai_description: '', // Optional now
          plot: {
            is_custom: true, // Flag at plot level for component resolution
            type: customSubsection.type,
            data: customSubsection.data ? [customSubsection.data] : [], // Optional data
            layout: {}, // Optional layout
          },
        },
      ],
    }

    // Determine positioning based on priority: first > after > last
    if (customSubsection.first) {
      // Insert at the beginning
      modifiedPlots.unshift(customSubsectionObject)
    } else if (customSubsection.after) {
      // Insert after the specified subsection
      const afterIndex = modifiedPlots.findIndex(
        (subsection) => subsection.title === customSubsection.after,
      )

      if (afterIndex !== -1) {
        modifiedPlots.splice(afterIndex + 1, 0, customSubsectionObject)
      } else {
        console.warn(
          `Subsection "${customSubsection.after}" not found for custom injection`,
        )
      }
    } else if (customSubsection.last) {
      // Insert at the end
      modifiedPlots.push(customSubsectionObject)
    } else {
      // Default: insert at the end if no positioning specified
      modifiedPlots.push(customSubsectionObject)
    }
  })

  return modifiedPlots
}

/**
 * CUSTOM ELEMENT DETECTION
 *
 * Simple utility to check if a plot object represents a custom JSX component.
 * We use the is_custom flag instead of checking for customJSX to maintain
 * consistency and simplify the detection logic.
 *
 * @param plot - The plot object to check
 * @returns True if it's a custom JSX element
 */
export const isCustomJSX = (
  plot: PlotlyContent | ExtendedPlotlyContent,
): plot is ExtendedPlotlyContent => {
  return plot && (plot as ExtendedPlotlyContent).is_custom === true
}

/**
 * CUSTOM CONFIGURATION EXTRACTION
 *
 * Extracts the type and data from a custom plot object for component
 * resolution and prop passing. This provides a clean interface for
 * the rendering logic to access custom component configuration.
 *
 * @param plot - The plot object containing custom JSX config
 * @returns Object with type and data for custom JSX
 */
export const getCustomJSXConfig = (
  plot: PlotlyContent | ExtendedPlotlyContent,
): CustomJSXConfig | null => {
  if (!isCustomJSX(plot)) {
    return null
  }

  return {
    type: plot.type || '',
    data: plot.data && plot.data.length > 0 ? plot.data[0] : {},
  }
}
